/**
 * Calendar Link Builders
 *
 * Pure builders for a booking's calendar entry: the Google and Outlook template URLs and the
 * ICS file. Nothing here opens a link, so the Mini App and the studio website both use them
 * (shared-client-screens D2); the Mini App's Telegram opener stays in googleCalendar.ts.
 * NOTE: This is separate from calendar.ts which handles calendar-grid utilities.
 *
 * @module utils/calendarLinks
 */

import { virtualPlatformLabel } from '@telify/shared/browser';
import type { BookingDetail } from '../types/bookingDetail';
import { getBrowserTimezone } from './calendar';

/**
 * Parameters for generating a Google Calendar event URL.
 */
export interface CalendarEventParams {
  /** ISO date string for scheduled appointment */
  scheduledAt: string;
  /** Duration in minutes */
  duration: number;
  /** Name of the service (used as event title) */
  serviceName: string;
  /** Provider name (included in details) */
  providerName: string | null;
  /** Whether this is a virtual/online session */
  isVirtual: boolean;
  /** Full address for physical locations */
  locationAddress: string | null;
  /** Platform name for virtual locations (e.g., "Zoom") */
  virtualPlatform: string | null;
  /** Join link for virtual sessions */
  virtualLink: string | null;
  /** Additional notes (preparation instructions, location notes) */
  notes: string | null;
}

/**
 * Format a Date object for Google Calendar URL.
 *
 * Google Calendar requires dates in format: YYYYMMDDTHHMMSSZ
 *
 * @param date - Date to format
 * @returns Formatted date string for Google Calendar
 */
function formatCalendarDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Generate a Google Calendar template URL for a booking.
 *
 * Creates a URL that opens Google Calendar with pre-filled event details.
 * Uses UTC timestamps with a ctz (timezone) parameter for proper display.
 *
 * @param params - Calendar event parameters
 * @returns Google Calendar template URL
 *
 * @example
 * const url = generateGoogleCalendarUrl({
 *   scheduledAt: '2026-01-20T14:00:00Z',
 *   duration: 60,
 *   serviceName: 'Group Breathwork',
 *   providerName: 'Maya Chen',
 *   isVirtual: false,
 *   locationAddress: '401 Congress Ave, Austin, TX',
 *   virtualPlatform: null,
 *   virtualLink: null,
 *   notes: 'Wear comfortable clothes',
 * });
 * // Opens Google Calendar with event pre-filled
 */
export function generateGoogleCalendarUrl(params: CalendarEventParams): string {
  const startDate = new Date(params.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + params.duration);

  const url = new URL('https://calendar.google.com/calendar/render');
  url.searchParams.set('action', 'TEMPLATE');
  url.searchParams.set('text', params.serviceName);
  url.searchParams.set(
    'dates',
    `${formatCalendarDate(startDate)}/${formatCalendarDate(endDate)}`
  );

  // Location: physical address or virtual platform
  const location = params.isVirtual
    ? params.virtualPlatform
      ? `Online on ${params.virtualPlatform}`
      : 'Online'
    : params.locationAddress;

  if (location) {
    url.searchParams.set('location', location);
  }

  // Details: provider name, notes, virtual link
  const detailParts = [
    params.providerName ? `With ${params.providerName}` : null,
    params.notes,
    params.virtualLink ? `Join: ${params.virtualLink}` : null,
  ].filter((part): part is string => Boolean(part));

  if (detailParts.length > 0) {
    url.searchParams.set('details', detailParts.join('\n\n'));
  }

  // Timezone: use browser's timezone for display hint
  url.searchParams.set('ctz', getBrowserTimezone());

  return url.toString();
}

/**
 * Generate an Outlook on the web compose URL for a booking: opens Outlook's
 * calendar with the event filled in, the same fields as the Google link.
 *
 * @param params - Calendar event parameters
 * @returns Outlook calendar compose URL
 */
export function generateOutlookCalendarUrl(params: CalendarEventParams): string {
  const startDate = new Date(params.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + params.duration);

  const url = new URL('https://outlook.live.com/calendar/0/action/compose');
  url.searchParams.set('rru', 'addevent');
  url.searchParams.set('subject', params.serviceName);
  url.searchParams.set('startdt', startDate.toISOString());
  url.searchParams.set('enddt', endDate.toISOString());

  const location = params.isVirtual
    ? params.virtualPlatform
      ? `Online on ${params.virtualPlatform}`
      : 'Online'
    : params.locationAddress;
  if (location) {
    url.searchParams.set('location', location);
  }

  const bodyParts = [
    params.providerName ? `With ${params.providerName}` : null,
    params.notes,
    params.virtualLink ? `Join: ${params.virtualLink}` : null,
  ].filter((part): part is string => Boolean(part));
  if (bodyParts.length > 0) {
    url.searchParams.set('body', bodyParts.join('\n\n'));
  }

  return url.toString();
}

/**
 * Escape special characters for ICS format.
 * ICS requires escaping commas, semicolons, and backslashes.
 */
function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\n/g, '\\n');
}

/**
 * Format a Date object for ICS format (YYYYMMDDTHHMMSSZ).
 */
function formatIcsDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Generate a unique ID for the calendar event.
 */
function generateEventUid(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${timestamp}-${random}@telify.cloud`;
}

/**
 * Generate an ICS file content for a booking.
 *
 * Creates an ICS (iCalendar) format string that can be downloaded
 * and opened by any calendar application (Apple Calendar, Google Calendar,
 * Outlook, etc.).
 *
 * @param params - Calendar event parameters
 * @returns ICS file content as string
 */
export function generateIcsContent(params: CalendarEventParams): string {
  const startDate = new Date(params.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + params.duration);

  // Location: physical address or virtual platform
  const location = params.isVirtual
    ? params.virtualPlatform
      ? `Online on ${params.virtualPlatform}`
      : 'Online'
    : params.locationAddress;

  // Description: provider name, notes, virtual link
  const descriptionParts = [
    params.providerName ? `With ${params.providerName}` : null,
    params.notes,
    params.virtualLink ? `Join: ${params.virtualLink}` : null,
  ].filter((part): part is string => Boolean(part));

  const description = descriptionParts.join('\n\n');

  // Build ICS content
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Telify//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${generateEventUid()}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    `SUMMARY:${escapeIcsText(params.serviceName)}`,
  ];

  if (description) {
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  }

  if (location) {
    lines.push(`LOCATION:${escapeIcsText(location)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return lines.join('\r\n');
}

// ── A booking's entry (shared-client-screens D4 "Calendar", rule 24) ──────────

/** An in-person entry's place: its name, street address and city, as far as they are known. */
export function calendarPlace(place: { name?: string | null; address?: string | null; city?: string | null }): string | null {
  return [place.name, place.address, place.city].filter(Boolean).join(', ') || null;
}

/**
 * A booking as a calendar entry, one rule for every Add to Calendar in the app and on the
 * website. In person it names the place with its street address and city; online the platform
 * and the meeting link. No notes: How to prepare reaches the client in Telegram and can be long
 * (open question 2, approved 2026-10-23).
 */
export function bookingCalendarEvent(b: Pick<BookingDetail, 'scheduledAt' | 'service' | 'provider' | 'location'>): CalendarEventParams {
  // service-by-phone D14: By phone, never the client's number, and no link (the entry can sync to a cloud calendar).
  const byPhone = b.location?.isByPhone === true;
  const online = !byPhone && b.location?.isVirtual ? b.location : null;
  return {
    scheduledAt: b.scheduledAt,
    duration: b.service.duration,
    serviceName: b.service.name,
    providerName: b.provider?.name ?? null,
    isVirtual: Boolean(online),
    locationAddress: byPhone ? 'By phone' : online || !b.location ? null : calendarPlace(b.location),
    // The platform's display name, never its key.
    virtualPlatform: online ? virtualPlatformLabel(online.virtualPlatform) : null,
    virtualLink: online?.virtualLink ?? null,
    notes: null,
  };
}

/** What every Add to Calendar says when Apple Calendar cannot get the booking's calendar file. */
export const CALENDAR_FILE_FAILED = "Couldn't open the calendar file. Please try again.";

/** Add to Calendar's Google Calendar and Outlook links for a booking, each opening it filled in. */
export function bookingCalendarLinks(b: Pick<BookingDetail, 'scheduledAt' | 'service' | 'provider' | 'location'>): { google: string; outlook: string } {
  const event = bookingCalendarEvent(b);
  return { google: generateGoogleCalendarUrl(event), outlook: generateOutlookCalendarUrl(event) };
}
